import React from 'react';

import { CompetitorBatchConfig, CompetitorData } from '../types';

interface VisibleCompetitorsState {
  visible: CompetitorData[];
  analyzedIds: string[];
}

/**
 * Reveals competitors one-by-one within each batch using batch.staggerMs,
 * and marks them "analyzed" after each batch's loadingDurationMs.
 * Mirrors the visibleCount + analyzedIds logic in CompetitorGrid so consumers
 * (including declarative celestial templates) can read ready-made lists off state.
 *
 * Visible list is in original order. Consumers that want newest-first
 * (as CompetitorGrid renders) should reverse it themselves.
 */
export function useVisibleCompetitors(
  batches: CompetitorBatchConfig[],
  active: boolean,
): VisibleCompetitorsState {
  const [visibleCount, setVisibleCount] = React.useState(0);
  const [analyzedIds, setAnalyzedIds] = React.useState<string[]>([]);

  const flatCompetitors = React.useMemo(() => {
    return batches.reduce<{ comp: CompetitorData; delay: number; loadingMs: number }[]>((acc, batch) => {
      const batchEnd = batch.delayMs + batch.loadingDurationMs;
      batch.competitors.forEach((c, cIdx) => {
        const delay = batch.delayMs + cIdx * batch.staggerMs;
        acc.push({ comp: c, delay, loadingMs: batchEnd - delay });
      });
      return acc;
    }, []);
  }, [batches]);

  React.useEffect(() => {
    if (!active) {
      setVisibleCount(0);
      setAnalyzedIds([]);
      return;
    }

    const timers: ReturnType<typeof setTimeout>[] = [];
    flatCompetitors.forEach((item, index) => {
      const revealTimer = setTimeout(() => {
        setVisibleCount((prev) => Math.max(prev, index + 1));
      }, item.delay);
      timers.push(revealTimer);

      const analyzedTimer = setTimeout(() => {
        setAnalyzedIds((prev) => (prev.includes(item.comp.id) ? prev : [...prev, item.comp.id]));
      }, item.delay + item.loadingMs);
      timers.push(analyzedTimer);
    });

    return () => timers.forEach(clearTimeout);
  }, [active, flatCompetitors]);

  const visible = React.useMemo(
    () => flatCompetitors.slice(0, visibleCount).map((i) => i.comp),
    [flatCompetitors, visibleCount],
  );

  return React.useMemo(() => ({ visible, analyzedIds }), [visible, analyzedIds]);
}
